import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Table,
  Typography,
  Chip,
  Select,
  Option,
  FormControl,
  IconButton,
  LinearProgress,
  Alert,
} from '@mui/joy';
import {
  DownloadSimple as DownloadIcon,
  Copy as ContentCopyIcon,
  XCircle as CancelIcon,
  Eye as VisibilityIcon,
} from '@phosphor-icons/react';
import { ordersApi, type Order } from '../../lib/distributorsApi';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  onViewOrder?: (order: Order) => void;
  refreshTrigger?: number;
}

export default function OrderList({ onViewOrder, refreshTrigger }: Props) {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;
  const requestVersion = useRef(0);

  const isAdmin = (user as any)?.role === 'admin' || (user as any)?.is_organization_admin;

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, methodFilter]);

  useEffect(() => {
    loadOrders();
    const refresh = () => { if (document.visibilityState === 'visible') void loadOrders(true); };
    const timer = window.setInterval(refresh, 30_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      ++requestVersion.current;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [statusFilter, methodFilter, currentPage, refreshTrigger]);

  const loadOrders = async (silent = false) => {
    const version = ++requestVersion.current;
    try {
      if (!silent) { setLoading(true); setError(null); }
      const params: any = { page: currentPage };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (methodFilter !== 'all') {
        params.submission_method = methodFilter;
      }
      const response = await ordersApi.list(params);
      if (version !== requestVersion.current) return;
      setOrders(response?.results || []);
      setTotalCount(response?.count || 0);
    } catch (err: any) {
      if (version !== requestVersion.current || silent) return;
      setError(err.response?.data?.detail || err.message || 'Failed to load orders');
      setOrders([]);
      setTotalCount(0);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const handleDownloadPdf = async (order: Order) => {
    try {
      const blob = await ordersApi.downloadPdf(order.id);
      const url = window.URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download PDF');
    }
  };

  const handleDuplicate = async (order: Order) => {
    try {
      const newOrder = await ordersApi.duplicate(order.id);
      await loadOrders();
      if (onViewOrder) {
        onViewOrder(newOrder);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to duplicate order');
    }
  };

  const handleCancel = async (order: Order) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      await ordersApi.cancel(order.id);
      await loadOrders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, any> = {
      draft: 'neutral',
      submitted: 'primary',
      processing: 'warning',
      completed: 'success',
      cancelled: 'danger',
    };
    return colors[status] || 'neutral';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography level="h3">Orders</Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="sm">
              <Select
                value={methodFilter}
                onChange={(_, value) => setMethodFilter(value as string)}
              >
                <Option value="all">All Methods</Option>
                <Option value="api">API</Option>
                <Option value="email">Email</Option>
              </Select>
            </FormControl>
            <FormControl size="sm">
              <Select
                value={statusFilter}
                onChange={(_, value) => setStatusFilter(value as string)}
              >
                <Option value="all">All Status</Option>
                <Option value="draft">Draft</Option>
                <Option value="submitted">Submitted</Option>
                <Option value="processing">Processing</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress />}

        {!loading && orders && orders.length === 0 && (
          <Alert color="neutral">
            No orders found. Create your first order to get started.
          </Alert>
        )}

        {!loading && orders && orders.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Distributor</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Confirmation #</th>
                  {isAdmin && <th>Created By</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders && orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <Typography level="body-md" fontWeight="lg">
                        {order.order_number}
                      </Typography>
                      {order.po_number && (
                        <Typography level="body-xs" color="neutral">
                          PO {order.po_number}
                        </Typography>
                      )}
                    </td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>{order.distributor_name || order.distributor_details?.name || 'N/A'}</td>
                    <td>${order.total_amount}</td>
                    <td>
                      <Chip
                        color={getStatusColor(order.status)}
                        size="sm"
                      >
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </Chip>
                      {order.external_status && (
                        <Chip size="sm" variant="outlined" color="neutral" sx={{ ml: 0.5 }}>
                          {order.external_status}
                        </Chip>
                      )}
                    </td>
                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={order.submission_method === 'api' ? 'primary' : 'neutral'}
                      >
                        {order.confirmation_number?.startsWith('MOCK-') ? 'Simulated' : order.submission_method === 'api' ? 'API' : 'Email'}
                      </Chip>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {order.confirmation_number || '—'}
                      </Typography>
                    </td>
                    {isAdmin && <td>{order.created_by_name || 'N/A'}</td>}
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {onViewOrder && (
                          <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={() => onViewOrder(order)}
                            title="View Details"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        )}
                        
                        {order.status === 'submitted' && order.submission_method !== 'api' && (
                          <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={() => handleDownloadPdf(order)}
                            title="Download PDF"
                          >
                            <DownloadIcon />
                          </IconButton>
                        )}
                        
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => handleDuplicate(order)}
                          title="Duplicate Order"
                          disabled={!!order.api_submission_state}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                        
                        {!order.api_submission_state && (order.status === 'draft' ||
                          (order.status === 'submitted' && isAdmin && order.submission_method !== 'api')) && (
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={() => handleCancel(order)}
                            title="Cancel Order"
                          >
                            <CancelIcon />
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}

        {/* Pagination */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="body-sm" color="neutral">
            {totalCount > 0
              ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} orders`
              : 'No orders'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
